import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { motion } from "framer-motion";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

import Welcome from "../../assets/home-img/welcome1.webp";
import ai_solutions from "../../assets/home-img/ai-solutions.png";
import database_migration from "../../assets/home-img/database-migration.webp";
import transform from "../../assets/home-img/transform1.webp";
import software from "../../assets/home-img/software.webp";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const slides = [
  {
    id: 1,
    title: "Welcome to Flint",
    subtitle: "Your Digital Growth Partner",
    description:
      "Partner with Flint InfoTech to unlock the full potential of technology. We deliver solutions that fuel growth, accelerate innovation, and ensure lasting digital success.",
    buttonText: "Discover More",
    buttonLink: "#about",
    image: Welcome,
  },
  {
    id: 2,
    title: "Transform Your Business",
    subtitle: "Next-Gen IT Solutions",
    description:
      "Stay ahead in the digital era with intelligent IT solutions that combine AI, automation, and cloud innovation.",
    buttonText: "Explore Solutions",
    buttonLink: "#solutions",
    image: transform,
  },
  {
    id: 3,
    title: "Web Application Development",
    subtitle: "Build Without Limits",
    description:
      "From custom applications to enterprise platforms, our software development services are designed to scale with your vision and meet your unique business needs.",
    buttonText: "Get Started",
    buttonLink: "/web-development",
    image: software,
  },
  {
    id: 4,
    title: "Database Migration",
    subtitle: "Move Data with Confidence",
    description:
      "Our database migration solutions ensure minimal downtime, enhanced security, and optimized performance for modern infrastructure.",
    buttonText: "Get Started",
    buttonLink: "/database-migration",
    image: database_migration,
  },
  {
    id: 5,
    title: "AI Solutions",
    subtitle: "Smarter Technology, Greater Possibilities",
    description:
      "Harness the power of AI with intelligent automation, machine learning, and predictive analytics. Streamline operations, uncover insights, and make faster, smarter decisions.",
    buttonText: "Get Started",
    buttonLink: "/bi-analytics",
    image: ai_solutions,
  },
];

const SplitText = ({ text, by = "word" }) => {
  const parts = by === "word" ? text.split(" ") : text.split("");
  return (
    <motion.span
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
      }}
      className="inline-block"
    >
      {parts.map((part, i) => (
        <motion.span
          key={i}
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.6, ease: "easeOut" },
            },
          }}
          className="inline-block mr-2"
        >
          {part}
        </motion.span>
      ))}
    </motion.span>
  );
};

export default function DashboardHero() {
  const [height, setHeight] = useState(window.innerHeight);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      className="relative w-full overflow-hidden bg-[#0d3559]"
      style={{ height: "87vh" }}
    >
      {/* Preload first image */}
      <img
        src={slides[0].image}
        alt="preload"
        className="hidden"
        loading="eager"
      />

      <Swiper
        modules={[Autoplay, EffectFade, Pagination]}
        effect="fade"
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        loop
        className="h-full"
        style={{
          "--swiper-pagination-color": "#0d3559",
          "--swiper-pagination-bullet-inactive-color": "#FFF",
          "--swiper-pagination-bullet-inactive-opacity": 0.5,
          "--swiper-pagination-bullet-size": "12px",
        }}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            {/* Background Image */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
                loading={slide.id === 1 ? "eager" : "lazy"}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20" />
            </div>

            {/* Slide Content */}
            <div className="relative z-10 flex flex-col justify-center h-full px-6 md:px-20 max-w-3xl text-white">
              <motion.h4
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg md:text-xl font-semibold tracking-wide text-[#38bdf8]"
              >
                {slide.subtitle}
              </motion.h4>

              <h2 className="text-4xl md:text-6xl font-extrabold leading-tight">
                <SplitText text={slide.title} by="word" />
              </h2>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-4 text-lg md:text-xl text-gray-200"
              >
                {slide.description}
              </motion.p>

              {slide.buttonText && (
                <motion.button
                  onClick={() => {
                    if (slide.buttonLink.startsWith("#")) {
                      const el = document.querySelector(slide.buttonLink);
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth" });
                      }
                    } else {
                      navigate(slide.buttonLink);
                    }
                  }}
                  className="mt-6 inline-block px-6 py-3 bg-[#0d3559] hover:border-2 text-white font-semibold rounded-lg shadow-lg max-w-[200px] text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {slide.buttonText}
                </motion.button>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}